import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { COUNTRIES, getBirthSubdivisionsForCountry, getProvincesForCountry, hasProvinceDropdown } from '../utils/countriesAndProvinces';
import sociImage from '../assets/images/soci.png';
import api, { resolveUploadUrl } from '../config/api';
import { compressImageForUpload } from '../utils/compressImageForUpload';
import { MEMBERSHIP_SEASON } from '../config/membershipSeason';
import './RegisterComplete.css';

const RegisterComplete = () => {
  const [formData, setFormData] = useState({
    // Informations de base
    civilite: 'Monsieur',
    firstName: '',
    lastName: '',
    sexe: 'Homme',
    dateOfBirth: '',
    
    // Lieu de naissance
    birthCountry: '',
    birthCity: '',
    birthCanton: '',
    
    
    // Contact
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Codice fiscale
    codiceFiscale: '',
    
    // Adresse
    street: '',
    streetNumber: '',
    city: '',
    canton: '',
    postalCode: '',
    country: 'Suisse',
    
    // Pièce d'identité
    identityType: 'Carte d\'identité',
    identityNumber: '',
    identityExpiry: '',
    identityIssueDate: '',
    identityIssueCanton: '',
    identityIssueCity: '',
    identityFileF: null,
    identityFileBack: null,
    
    // Tessera del tifoso / Juventus Card
    hasTessera: false,
    wantsToGetTessera: false,
    tesseraNumber: '',
    tesseraExpiry: '',
    tesseraPIN: '',
    tesseraFile: null,
    
    // Abonnement
    hasSubscription: false,
    tribune: '',
    settore: '',
    fila: '',
    posto: '',
    
    // Carte Black & White
    hasBlackWhite: false,
    blackWhiteNumber: '',
    blackWhiteExpiry: '',
    blackWhiteFile: null,
    
    // Consentements
    marketing: false,
    profiling: false,
    newsletter: false,
    minorAuth: false,
    imageRights: false,
    discharge: false,
    termsAccepted: false,
    
    // Mineur
    isMinor: false,
    parentEmail: '',
    parentPhone: '',
    parentFirstName: '',
    parentLastName: '',
    parentSexe: 'Homme',
    parentBirthDate: '',
    parentBirthCity: '',
    parentBirthCanton: '',
    parentBirthCountry: '',
    parentIdentityFile: null,
    
    // Autre fan club
    otherFanClub: 'non',
    hasUnsubscribed: false,
    
    // Type de cotisation et paiement
    membershipType: '',
    paymentMethod: '',
    currency: 'CHF'
  });

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [registerSociImageUrl, setRegisterSociImageUrl] = useState(null);

  const filePickSeqRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/settings/register-soci-image');
        if (!cancelled && res.data?.registerSociImageUrl) {
          setRegisterSociImageUrl(res.data.registerSociImageUrl);
        }
      } catch {
        /* bannière par défaut (asset statique) */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // State for expandable consent sections
  const [expandedConsents, setExpandedConsents] = useState({
    marketing: false,
    profiling: false,
    imageRights: false,
    newsletter: false,
    discharge: false
  });

  const registrationPricingMobileLabels = {
    adult: `${t('registerForm.thAdult')} (${t('registerForm.thPriceChfEur')})`,
    under14: `${t('registerForm.thUnder14')} (${t('registerForm.thPriceChfEur')})`,
    under5: `${t('registerForm.thUnder5')} (${t('registerForm.pricingUnder5Cell')})`,
  };

  const toggleConsent = (consentType) => {
    setExpandedConsents(prev => ({
      ...prev,
      [consentType]: !prev[consentType]
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      const f = files?.[0];
      if (!f) {
        setFormData((prev) => ({ ...prev, [name]: null }));
        return;
      }
      const seq = (filePickSeqRef.current[name] = (filePickSeqRef.current[name] || 0) + 1);
      void (async () => {
        try {
          const processed = await compressImageForUpload(f);
          if (filePickSeqRef.current[name] !== seq) return;
          setFormData((prev) => ({ ...prev, [name]: processed }));
        } catch {
          if (filePickSeqRef.current[name] !== seq) return;
          setFormData((prev) => ({ ...prev, [name]: f }));
        }
      })();
    } else {
      setFormData(prev => {
        const next = { ...prev, [name]: value };
        if (name === 'birthCountry') next.birthCanton = '';
        if (name === 'parentBirthCountry') next.parentBirthCanton = '';
        if (name === 'country') next.canton = '';
        if (name === 'civilite') {
          if (value === 'Madame' || value === 'Signora') {
            next.sexe = 'Femme';
          } else if (value === 'Monsieur' || value === 'Signore') {
            next.sexe = 'Homme';
          }
        }
        return next;
      });
    }
  };

  // Calculer si la personne est mineure et le type de cotisation suggéré
  React.useEffect(() => {
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const currentYear = today.getFullYear();
      
      // Calculer l'âge
      let age = currentYear - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      const isMinor = age < 18;
      
      // Déterminer le type de cotisation suggéré selon la saison 2026-2027
      // Under5: né après le 1er juillet 2022 (gratuit)
      // Under14: né après le 1er juillet 2013
      // Adulte (JOFC Adult): sinon
      
      const cutoffUnder5 = new Date(2022, 6, 1); // 1er juillet 2022
      const cutoffUnder14 = new Date(2013, 6, 1); // 1er juillet 2013
      
      let suggestedType = '';
      if (birthDate > cutoffUnder5) {
        suggestedType = 'under5';
      } else if (birthDate > cutoffUnder14) {
        suggestedType = 'under14plus';
      } else {
        suggestedType = 'adult';
      }
      
      setFormData(prev => ({ ...prev, isMinor, suggestedMembershipType: suggestedType }));
    }
  }, [formData.dateOfBirth]);

  // Calculer le prix selon le type de cotisation et le mode de paiement
  const calculatePrice = () => {
    if (!formData.membershipType || !formData.paymentMethod) return null;
    
    const prices = {
      adult: {
        'E-banking': { CHF: 40, EUR: 40 },
        Twint: { CHF: 40 },
        'Carte Revolut': { CHF: 40, EUR: 40 },
        'Carte de crédit': { CHF: 40, EUR: 40 },
      },
      under14plus: {
        'E-banking': { CHF: 30, EUR: 30 },
        'Twint': { CHF: 30 },
        'Carte Revolut': { CHF: 30, EUR: 30 },
        'Carte de crédit': { CHF: 30, EUR: 30 }
      },
      'under5': {
        'all': { CHF: 0, EUR: 0 }
      }
    };
    
    return prices[formData.membershipType]?.[formData.paymentMethod]?.[formData.currency] || 0;
  };

  const validateForm = () => {
    const newErrors = [];
    const err = (key) => t(`registerForm.errors.${key}`);

    if (!formData.civilite) newErrors.push(err('civilite'));
    if (!formData.firstName) newErrors.push(err('firstName'));
    if (!formData.lastName) newErrors.push(err('lastName'));
    if (!formData.sexe) newErrors.push(err('sexe'));
    if (!formData.dateOfBirth) newErrors.push(err('dateOfBirth'));
    if (!formData.birthCountry) newErrors.push(err('birthCountry'));
    if (!formData.birthCity) newErrors.push(err('birthCity'));
    if (!formData.phone) newErrors.push(err('phone'));
    if (!formData.email) newErrors.push(err('email'));
    if (!formData.password) newErrors.push(err('password'));
    if (formData.password !== formData.confirmPassword) {
      newErrors.push(err('passwordMismatch'));
    }
    if (!formData.street) newErrors.push(err('street'));
    if (!formData.streetNumber) newErrors.push(err('streetNumber'));
    if (!formData.city) newErrors.push(err('city'));
    if (!formData.postalCode) newErrors.push(err('postalCode'));
    if (!formData.identityType) newErrors.push(err('identityType'));
    if (!formData.identityNumber) newErrors.push(err('identityNumber'));
    if (!formData.identityExpiry) newErrors.push(err('identityExpiry'));
    if (!formData.identityIssueDate) newErrors.push(err('identityIssueDate'));
    if (!formData.identityIssueCanton) newErrors.push(err('identityIssueCanton'));
    if (!formData.identityIssueCity) newErrors.push(err('identityIssueCity'));
    if (!formData.identityFileF) newErrors.push(err('identityFileF'));

    if (formData.country === 'Italie' && !formData.codiceFiscale) {
      newErrors.push(err('codiceItaly'));
    }

    if (formData.hasTessera) {
      if (!formData.tesseraNumber) newErrors.push(err('tesseraNumber'));
      if (!formData.tesseraExpiry) newErrors.push(err('tesseraExpiry'));
      if (!formData.tesseraPIN) newErrors.push(err('tesseraPIN'));
      if (!formData.tesseraFile) newErrors.push(err('tesseraFile'));
    }

    if (formData.hasSubscription) {
      if (!formData.tribune) newErrors.push(err('tribune'));
      if (!formData.settore) newErrors.push(err('settore'));
      if (!formData.fila) newErrors.push(err('fila'));
      if (!formData.posto) newErrors.push(err('posto'));
    }

    if (formData.hasBlackWhite) {
      if (!formData.blackWhiteNumber) newErrors.push(err('blackWhiteNumber'));
      if (!formData.blackWhiteExpiry) newErrors.push(err('blackWhiteExpiry'));
      if (!formData.blackWhiteFile) newErrors.push(err('blackWhiteFile'));
    }

    if (formData.isMinor) {
      if (!formData.parentEmail) newErrors.push(err('parentEmail'));
      if (!formData.parentPhone) newErrors.push(err('parentPhone'));
      if (!formData.parentFirstName) newErrors.push(err('parentFirstName'));
      if (!formData.parentLastName) newErrors.push(err('parentLastName'));
      if (!formData.parentIdentityFile) newErrors.push(err('parentIdentityFile'));
    }

    if (!formData.discharge) newErrors.push(err('discharge'));
    if (!formData.membershipType) newErrors.push(err('membershipType'));
    if (!formData.paymentMethod) newErrors.push(err('paymentMethod'));
    if (formData.otherFanClub === '') newErrors.push(err('otherFanClub'));

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }
    
    setLoading(true);

    // Préparer les données
    // Convertir civilite: "Madame" -> "Mme", "Monsieur" -> "M"
    const civiliteMap = {
      'Madame': 'Mme',
      'Monsieur': 'M'
    };
    
    const userData = {
      email: formData.email,
      password: formData.password,
      civilite: civiliteMap[formData.civilite] || formData.civilite,
      firstName: formData.firstName,
      lastName: formData.lastName,
      sexe: formData.sexe,
      dateOfBirth: formData.dateOfBirth, // Format ISO8601 depuis input type="date"
      birthPlace: {
        country: formData.birthCountry,
        city: formData.birthCity,
        canton: formData.birthCanton
      },
      phone: formData.phone,
      address: {
        street: formData.street,
        streetNumber: formData.streetNumber,
        city: formData.city,
        canton: formData.canton,
        postalCode: formData.postalCode,
        country: formData.country
      },
      identity: {
        type: formData.identityType,
        number: formData.identityNumber,
        expiryDate: formData.identityExpiry,
        issueDate: formData.identityIssueDate,
        issueCanton: formData.identityIssueCanton,
        issueCity: formData.identityIssueCity
      },
      codiceFiscale: formData.codiceFiscale,
      juventusFanCard: {
        has: formData.hasTessera,
        number: formData.hasTessera ? formData.tesseraNumber.trim() : '',
        expiryDate: formData.tesseraExpiry,
        webticketPIN: formData.tesseraPIN
      },
      subscription: {
        has: formData.hasSubscription,
        tribune: formData.tribune,
        settore: formData.settore,
        fila: formData.fila,
        posto: formData.posto
      },
      blackWhiteCard: {
        has: formData.hasBlackWhite,
        number: formData.blackWhiteNumber,
        expiryDate: formData.blackWhiteExpiry
      },
      isMinor: formData.isMinor ? 'true' : 'false',
      parentInfo: formData.isMinor ? {
        email: formData.parentEmail,
        phone: formData.parentPhone,
        firstName: formData.parentFirstName,
        lastName: formData.parentLastName,
        sexe: formData.parentSexe,
        dateOfBirth: formData.parentBirthDate,
        birthCity: formData.parentBirthCity,
        birthCanton: formData.parentBirthCanton,
        birthCountry: formData.parentBirthCountry,
      } : null,
      consents: {
        marketing: formData.marketing,
        profiling: formData.profiling,
        minorAuthorization: formData.minorAuth,
        imageRights: formData.imageRights,
        termsAccepted: true // Automatiquement accepté en soumettant le formulaire (voir message en bas)
      },
      newsletter: formData.newsletter,
      otherFanClub: {
        isMember: formData.otherFanClub === 'oui',
        hasUnsubscribed: formData.hasUnsubscribed
      },
      preferredLanguage: language,
      season: MEMBERSHIP_SEASON,
      payment: formData.paymentMethod ? {
        method: formData.paymentMethod,
        type:
          formData.membershipType === 'adult' ? 'STANDARD' :
          formData.membershipType === 'under14plus' ? 'Under14' :
          formData.membershipType === 'under5' ? 'Under5' :
          'STANDARD',
        amount: calculatePrice() || 0,
        currency: formData.currency || 'CHF',
        status: 'pending',
        date: new Date()
      } : undefined,
      // Ajouter les fichiers pour l'upload
      identityFileF: formData.identityFileF,
      identityFileBack: formData.identityFileBack,
      tesseraFile: formData.tesseraFile,
      blackWhiteFile: formData.blackWhiteFile,
      parentIdentityFile: formData.parentIdentityFile
    };

    const result = await register(userData);

    if (result.success) {
      alert(t('registerForm.alertSuccess'));
      navigate('/');
    } else {
      // Parser les erreurs pour les afficher de manière plus claire
      let errorMessages = [];

      if (result.errorKey === 'payloadTooLarge') {
        errorMessages = [
          result.error && String(result.error).trim()
            ? String(result.error)
            : t('registerForm.errorPayloadTooLarge'),
        ];
      } else if (result.errors && Array.isArray(result.errors)) {
        errorMessages = result.errors;
      } else if (result.error) {
        // Si l'erreur est une string, essayer de la parser
        if (typeof result.error === 'string') {
          // Si l'erreur contient des points-virgules, c'est probablement plusieurs erreurs
          if (result.error.includes('; ')) {
            errorMessages = result.error.split('; ');
          } else if (result.error.includes(', ')) {
            errorMessages = result.error.split(', ');
          } else {
            errorMessages = [result.error];
          }
        } else {
          errorMessages = [result.error];
        }
      } else {
        errorMessages = [t('registerForm.errorGenericSubmit')];
      }
      
      setErrors(errorMessages);
      window.scrollTo(0, 0);
    }

    setLoading(false);
  };

  return (
    <div className="register-complete-page">
      <div className="register-complete-container">
        <h1>{t('registerForm.pageTitle')}</h1>
        
        {errors.length > 0 && (
          <div className="error-box">
            <h3>{t('registerForm.validationTitle')}</h3>
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="register-complete-form">
          
          {/* Informations personnelles */}
          <div className="form-section">
            <h2>{t('registerForm.sectionPersonal')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelCivilite')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="civilite"
                    value="Madame"
                    checked={formData.civilite === 'Madame'}
                    onChange={handleChange}
                    required
                  />
                  <span>{t('registerForm.madame')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="civilite"
                    value="Monsieur"
                    checked={formData.civilite === 'Monsieur'}
                    onChange={handleChange}
                    required
                  />
                  <span>{t('registerForm.monsieur')}</span>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('registerForm.labelLastName')}</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderNameDoc')}
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelFirstName')}</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderNameDoc')}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelSexe')}</label>
              <select name="sexe" value={formData.sexe} onChange={handleChange} required>
                <option value="Homme">{t('registerForm.sexeHomme')}</option>
                <option value="Femme">{t('registerForm.sexeFemme')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelDob')}</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Lieu de naissance : pays → canton ou département → ville */}
          <div className="form-section">
            <h2>{t('registerForm.sectionBirth')}</h2>

            <div className="form-group">
              <label>{t('registerForm.labelBirthCountry')}</label>
              <select
                name="birthCountry"
                value={formData.birthCountry}
                onChange={handleChange}
                required
              >
                <option value="">{t('registerForm.selectCountry')}</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelBirthCanton')}</label>
              {hasProvinceDropdown(formData.birthCountry) ? (
                <select name="birthCanton" value={formData.birthCanton} onChange={handleChange}>
                  <option value="">{t('registerForm.selectDot')}</option>
                  {(getBirthSubdivisionsForCountry(formData.birthCountry) || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="birthCanton"
                  value={formData.birthCanton}
                  onChange={handleChange}
                  placeholder={t('registerForm.birthSubdivisionPlaceholderOther')}
                />
              )}
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelBirthCity')}</label>
              <input
                type="text"
                name="birthCity"
                value={formData.birthCity}
                onChange={handleChange}
                required
                placeholder={t('registerForm.placeholderBirthCity')}
              />
            </div>
          </div>

          {/* Email et Mobile */}
          <div className="form-section">
            <h2>{t('registerForm.sectionEmailMobile')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelEmail')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t('registerForm.placeholderEmail')}
              />
              <small>{t('registerForm.emailHelp')}</small>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelMobile')}</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+41791234567"
              />
              <small>{t('registerForm.phoneFormat')}</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('registerForm.labelPassword')}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength="6"
                  placeholder={t('registerForm.placeholderPassword')}
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelConfirmPassword')}</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength="6"
                  placeholder={t('registerForm.placeholderConfirmPassword')}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelCodice')}</label>
              <input
                type="text"
                name="codiceFiscale"
                value={formData.codiceFiscale}
                onChange={handleChange}
                placeholder={t('registerForm.placeholderCodice')}
                maxLength="16"
              />
              <small dangerouslySetInnerHTML={{ __html: t('registerForm.codiceHelp') }} />
            </div>
          </div>

          {/* Adresse de résidence */}
          <div className="form-section">
            <h2>{t('registerForm.sectionAddress')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelStreet')}</label>
              <div className="address-row">
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderStreet')}
                  style={{flex: 3}}
                />
                <input
                  type="text"
                  name="streetNumber"
                  value={formData.streetNumber}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderStreetNo')}
                  style={{flex: 1, maxWidth: '100px'}}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('registerForm.labelPostal')}</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                  placeholder="1000"
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelCityResidence')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderCity')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('registerForm.labelResidenceCountry')}</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t('registerForm.selectCountry')}</option>
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelResidenceCanton')}</label>
                {hasProvinceDropdown(formData.country) ? (
                  <select
                    name="canton"
                    value={formData.canton}
                    onChange={handleChange}
                  >
                    <option value="">{t('registerForm.selectDot')}</option>
                    {(getProvincesForCountry(formData.country) || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="canton"
                    value={formData.canton}
                    onChange={handleChange}
                    placeholder="VD, VS, GE..."
                    maxLength="3"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Pièce d'identité */}
          <div className="form-section">
            <h2>{t('registerForm.sectionIdentity')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelIdType')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="identityType"
                    value="Carte d'identité"
                    checked={formData.identityType === 'Carte d\'identité'}
                    onChange={handleChange}
                    required
                  />
                  <span>{t('registerForm.idCard')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="identityType"
                    value="Passeport"
                    checked={formData.identityType === 'Passeport'}
                    onChange={handleChange}
                    required
                  />
                  <span>{t('registerForm.passport')}</span>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('registerForm.labelIdNumber')}</label>
                <input
                  type="text"
                  name="identityNumber"
                  value={formData.identityNumber}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderIdNumber')}
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelIdExpiry')}</label>
                <input
                  type="date"
                  name="identityExpiry"
                  value={formData.identityExpiry}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('registerForm.labelIdIssueDate')}</label>
                <input
                  type="date"
                  name="identityIssueDate"
                  value={formData.identityIssueDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelIdIssueCanton')}</label>
                <input
                  type="text"
                  name="identityIssueCanton"
                  value={formData.identityIssueCanton}
                  onChange={handleChange}
                  required
                  placeholder={t('registerForm.placeholderIdIssueCanton')}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelIdIssueCity')}</label>
              <input
                type="text"
                name="identityIssueCity"
                value={formData.identityIssueCity}
                onChange={handleChange}
                required
                placeholder={t('registerForm.placeholderIdIssueCity')}
              />
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelIdCopy')}</label>
              <p
                className="upload-instructions"
                dangerouslySetInnerHTML={{ __html: t('registerForm.uploadIdInstructions') }}
              />
              <input
                type="file"
                name="identityFileF"
                onChange={handleChange}
                accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/heic,image/heif"
                required
              />
              <small>{t('registerForm.fileHintFront')}</small>
            </div>

            {formData.identityType === 'Carte d\'identité' && (
              <div className="form-group">
                <label>{t('registerForm.labelIdVerso')}</label>
                <input
                  type="file"
                  name="identityFileBack"
                  onChange={handleChange}
                  accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/heic,image/heif"
                />
                <small>{t('registerForm.idVersoHint')}</small>
              </div>
            )}
          </div>

          {/* VOTRE JUVENTUS CARD OU TESSERA DEL TIFOSO */}
          <div className="form-section">
            <h2>{t('registerForm.sectionJuveCard')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelHasTessera')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="hasTessera"
                    value="true"
                    checked={formData.hasTessera === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasTessera: true, wantsToGetTessera: false }))}
                  />
                  <span>{t('registerForm.yes')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="hasTessera"
                    value="false"
                    checked={formData.hasTessera === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasTessera: false }))}
                  />
                  <span>{t('registerForm.no')}</span>
                </label>
              </div>
            </div>

            {formData.hasTessera && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('registerForm.labelTesseraNo')}</label>
                    <input
                      type="text"
                      name="tesseraNumber"
                      value={formData.tesseraNumber}
                      onChange={handleChange}
                      required={formData.hasTessera}
                      placeholder={t('registerForm.tesseraPlaceholder')}
                      maxLength="12"
                    />
                    <small>{t('registerForm.tesseraFormat')}</small>
                  </div>

                  <div className="form-group">
                    <label>{t('registerForm.labelTesseraExpiry')}</label>
                    <input
                      type="date"
                      name="tesseraExpiry"
                      value={formData.tesseraExpiry}
                      onChange={handleChange}
                      required={formData.hasTessera}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('registerForm.labelTesseraPin')}</label>
                  <input
                    type="text"
                    name="tesseraPIN"
                    value={formData.tesseraPIN}
                    onChange={handleChange}
                    required={formData.hasTessera}
                    placeholder={t('registerForm.tesseraPinPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('registerForm.labelTesseraCopy')}</label>
                  <p
                    className="upload-instructions"
                    dangerouslySetInnerHTML={{ __html: t('registerForm.tesseraUpload') }}
                  />
                  <input
                    type="file"
                    name="tesseraFile"
                    onChange={handleChange}
                    accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/heic,image/heif"
                    required={formData.hasTessera}
                  />
                  <small>{t('registerForm.tesseraSmall')}</small>
                </div>
              </>
            )}

            {!formData.hasTessera && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="wantsToGetTessera"
                    checked={formData.wantsToGetTessera}
                    onChange={handleChange}
                  />
                  <span>{t('registerForm.wantTessera')}</span>
                </label>
                {formData.wantsToGetTessera && (
                  <div className="info-box">
                    <p dangerouslySetInnerHTML={{ __html: t('registerForm.tesseraInfoBox') }} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ABONNEMENT */}
          <div className="form-section">
            <h2>{t('registerForm.sectionSubscription')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelHasSubscription')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="hasSubscription"
                    value="true"
                    checked={formData.hasSubscription === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasSubscription: true }))}
                  />
                  <span>{t('registerForm.yes')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="hasSubscription"
                    value="false"
                    checked={formData.hasSubscription === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasSubscription: false }))}
                  />
                  <span>{t('registerForm.no')}</span>
                </label>
              </div>
            </div>

            {formData.hasSubscription && (
              <>
                <div className="form-group">
                  <label>{t('registerForm.labelTribune')}</label>
                  <select name="tribune" value={formData.tribune} onChange={handleChange} required={formData.hasSubscription}>
                    <option value="">{t('registerForm.selectTribune')}</option>
                    <option value="Tribuna Sud 1°">Tribuna Sud 1°</option>
                    <option value="Tribuna Sud 2°">Tribuna Sud 2°</option>
                    <option value="Tribuna Nord 1°">Tribuna Nord 1°</option>
                    <option value="Tribuna Nord 2°">Tribuna Nord 2°</option>
                    <option value="Tribuna Family">Tribuna Family</option>
                    <option value="Tribuna Est Centrale 1°">Tribuna Est Centrale 1°</option>
                    <option value="Tribuna Est Centrale 2°">Tribuna Est Centrale 2°</option>
                    <option value="Tribuna Est Laterale 1°">Tribuna Est Laterale 1°</option>
                    <option value="Tribuna Est Laterale 2°">Tribuna Est Laterale 2°</option>
                    <option value="Tribuna Est 2 Laterale (Nord Est)">Tribuna Est 2 Laterale (Nord Est)</option>
                    <option value="Tribuna Est 2 Laterale (Sud Est)">Tribuna Est 2 Laterale (Sud Est)</option>
                    <option value="Tribuna Ovest Laterale 1°">Tribuna Ovest Laterale 1°</option>
                    <option value="Tribuna Ovest Laterale 2°">Tribuna Ovest Laterale 2°</option>
                    <option value="Tribuna Ovest Centrale 2°">Tribuna Ovest Centrale 2°</option>
                    <option value="Tribuna Sivori 1°">Tribuna Sivori 1°</option>
                    <option value="Tribuna Sivori 2°">Tribuna Sivori 2°</option>
                    <option value="Tribuna Boniperti 1°">Tribuna Boniperti 1°</option>
                    <option value="Tribuna T100">Tribuna T100</option>
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('registerForm.labelSettore')}</label>
                    <input
                      type="number"
                      name="settore"
                      value={formData.settore}
                      onChange={handleChange}
                      required={formData.hasSubscription}
                      placeholder="101-126, 201-232"
                      min="101"
                      max="232"
                    />
                    <small>{t('registerForm.settoreHint')}</small>
                  </div>

                  <div className="form-group">
                    <label>{t('registerForm.labelFila')}</label>
                    <input
                      type="text"
                      name="fila"
                      value={formData.fila}
                      onChange={handleChange}
                      required={formData.hasSubscription}
                      placeholder={t('registerForm.placeholderFila')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('registerForm.labelPosto')}</label>
                  <input
                    type="number"
                    name="posto"
                    value={formData.posto}
                    onChange={handleChange}
                    required={formData.hasSubscription}
                    placeholder="1-58"
                    min="1"
                    max="58"
                  />
                  <small>{t('registerForm.postoHint')}</small>
                </div>
              </>
            )}
          </div>

          {/* CARTE BLACK & WHITE OU J1897 */}
          <div className="form-section">
            <h2>{t('registerForm.sectionBW')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.labelHasBW')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="hasBlackWhite"
                    value="true"
                    checked={formData.hasBlackWhite === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasBlackWhite: true }))}
                  />
                  <span>{t('registerForm.yes')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="hasBlackWhite"
                    value="false"
                    checked={formData.hasBlackWhite === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasBlackWhite: false }))}
                  />
                  <span>{t('registerForm.no')}</span>
                </label>
              </div>
            </div>

            {formData.hasBlackWhite && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('registerForm.labelBwNumber')}</label>
                    <input
                      type="text"
                      name="blackWhiteNumber"
                      value={formData.blackWhiteNumber}
                      onChange={handleChange}
                      required={formData.hasBlackWhite}
                      placeholder={t('registerForm.placeholderCardNo')}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('registerForm.labelBwExpiry')}</label>
                    <input
                      type="date"
                      name="blackWhiteExpiry"
                      value={formData.blackWhiteExpiry}
                      onChange={handleChange}
                      required={formData.hasBlackWhite}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('registerForm.labelBwCopy')}</label>
                  <p
                    className="upload-instructions"
                    dangerouslySetInnerHTML={{ __html: t('registerForm.tesseraUpload') }}
                  />
                  <input
                    type="file"
                    name="blackWhiteFile"
                    onChange={handleChange}
                    accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/heic,image/heif"
                    required={formData.hasBlackWhite}
                  />
                  <small>{t('registerForm.tesseraSmall')}</small>
                </div>
              </>
            )}
          </div>

          {/* CONDITIONS GÉNÉRALES ET CONFIDENTIALITÉ */}
          <div className="form-section">
            <h2>{t('registerForm.sectionConsents')}</h2>
            
            <div className="form-group consent-group">
              <label className="consent-title">{t('registerForm.marketingTitle')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="marketing"
                    value="true"
                    checked={formData.marketing === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, marketing: true }))}
                  />
                  <span>{t('registerForm.accept')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="marketing"
                    value="false"
                    checked={formData.marketing === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, marketing: false }))}
                  />
                  <span>{t('registerForm.refuse')}</span>
                </label>
              </div>
              <p className="consent-text preview" onClick={() => toggleConsent('marketing')}>
                {t('registerForm.consentMarketingPrev')}
                <span className="expand-icon">{expandedConsents.marketing ? '▼' : '▶'}</span>
              </p>
              {expandedConsents.marketing && (
                <p className="consent-text expanded">
                  {t('registerForm.consentMarketingExp')}
                </p>
              )}
            </div>

            <div className="form-group consent-group">
              <label className="consent-title">{t('registerForm.profilingTitle')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="profiling"
                    value="true"
                    checked={formData.profiling === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, profiling: true }))}
                  />
                  <span>{t('registerForm.accept')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="profiling"
                    value="false"
                    checked={formData.profiling === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, profiling: false }))}
                  />
                  <span>{t('registerForm.refuse')}</span>
                </label>
              </div>
              <p className="consent-text preview" onClick={() => toggleConsent('profiling')}>
                {t('registerForm.consentProfilingPrev')}
                <span className="expand-icon">{expandedConsents.profiling ? '▼' : '▶'}</span>
              </p>
              {expandedConsents.profiling && (
                <p className="consent-text expanded">
                  {t('registerForm.consentProfilingExp')}
                </p>
              )}
            </div>

            <div className="form-group consent-group">
              <label className="consent-title">{t('registerForm.imageTitle')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="imageRights"
                    value="true"
                    checked={formData.imageRights === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, imageRights: true }))}
                  />
                  <span>{t('registerForm.accept')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="imageRights"
                    value="false"
                    checked={formData.imageRights === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, imageRights: false }))}
                  />
                  <span>{t('registerForm.refuse')}</span>
                </label>
              </div>
              <p className="consent-text preview" onClick={() => toggleConsent('imageRights')}>
                {t('registerForm.consentImagePrev')}
                <span className="expand-icon">{expandedConsents.imageRights ? '▼' : '▶'}</span>
              </p>
              {expandedConsents.imageRights && (
                <p className="consent-text expanded">
                  {t('registerForm.consentImageExp')}
                </p>
              )}
            </div>

            <div className="form-group consent-group">
              <label className="consent-title">{t('registerForm.newsletterTitle')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="newsletter"
                    value="true"
                    checked={formData.newsletter === true}
                    onChange={() => setFormData(prev => ({ ...prev, newsletter: true }))}
                  />
                  <span>{t('registerForm.accept')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="newsletter"
                    value="false"
                    checked={formData.newsletter === false}
                    onChange={() => setFormData(prev => ({ ...prev, newsletter: false }))}
                  />
                  <span>{t('registerForm.refuse')}</span>
                </label>
              </div>
              <p className="consent-text preview" onClick={() => toggleConsent('newsletter')}>
                {t('registerForm.consentNewsletterPrev')}
                <span className="expand-icon">{expandedConsents.newsletter ? '▼' : '▶'}</span>
              </p>
              {expandedConsents.newsletter && (
                <p className="consent-text expanded">
                  {t('registerForm.consentNewsletterExp')}
                </p>
              )}
            </div>
          </div>

          {/* COORDONNÉES PARENT (si enfant mineur) */}
          {formData.isMinor && (
            <div className="form-section highlight-section">
              <h2>{t('registerForm.sectionParent')}</h2>
              <p className="section-note">
                {t('registerForm.parentNote')}
              </p>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('registerForm.labelParentLastName')}</label>
                  <input
                    type="text"
                    name="parentLastName"
                    value={formData.parentLastName}
                    onChange={handleChange}
                    required={formData.isMinor}
                    placeholder={t('registerForm.placeholderParentLast')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('registerForm.labelParentFirstName')}</label>
                  <input
                    type="text"
                    name="parentFirstName"
                    value={formData.parentFirstName}
                    onChange={handleChange}
                    required={formData.isMinor}
                    placeholder={t('registerForm.placeholderParentFirst')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelParentSexe')}</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="parentSexe"
                      value="Homme"
                      checked={formData.parentSexe === 'Homme'}
                      onChange={handleChange}
                    />
                    <span>{t('registerForm.sexeHomme')}</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="parentSexe"
                      value="Femme"
                      checked={formData.parentSexe === 'Femme'}
                      onChange={handleChange}
                    />
                    <span>{t('registerForm.sexeFemme')}</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelParentDob')}</label>
                <input
                  type="date"
                  name="parentBirthDate"
                  value={formData.parentBirthDate}
                  onChange={handleChange}
                  required={formData.isMinor}
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelParentPhone')}</label>
                <input
                  type="tel"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleChange}
                  required={formData.isMinor}
                  placeholder="+41791234567"
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelParentEmail')}</label>
                <input
                  type="email"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleChange}
                  required={formData.isMinor}
                  placeholder="parent@email.com"
                />
              </div>

              <div className="form-section-subtitle">{t('registerForm.parentBirthSubtitle')}</div>

              <div className="form-group">
                <label>{t('registerForm.labelVille')}</label>
                <input
                  type="text"
                  name="parentBirthCity"
                  value={formData.parentBirthCity}
                  onChange={handleChange}
                  required={formData.isMinor}
                  placeholder={t('registerForm.placeholderVilleNaissance')}
                />
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelPays')}</label>
                <select
                  name="parentBirthCountry"
                  value={formData.parentBirthCountry}
                  onChange={handleChange}
                  required={formData.isMinor}
                >
                  <option value="">{t('registerForm.selectCountry')}</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelCantonRegion')}</label>
                {hasProvinceDropdown(formData.parentBirthCountry) ? (
                  <select
                    name="parentBirthCanton"
                    value={formData.parentBirthCanton}
                    onChange={handleChange}
                    required={formData.isMinor}
                  >
                    <option value="">{t('registerForm.selectDot')}</option>
                    {(getBirthSubdivisionsForCountry(formData.parentBirthCountry) || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="parentBirthCanton"
                    value={formData.parentBirthCanton}
                    onChange={handleChange}
                    required={formData.isMinor}
                    placeholder={t('registerForm.birthSubdivisionPlaceholderOther')}
                  />
                )}
              </div>

              <div className="form-group">
                <label>{t('registerForm.labelParentId')}</label>
                <p
                  className="upload-instructions"
                  dangerouslySetInnerHTML={{ __html: t('registerForm.parentIdUpload') }}
                />
                <input
                  type="file"
                  name="parentIdentityFile"
                  onChange={handleChange}
                  accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/heic,image/heif"
                  required={formData.isMinor}
                />
                <small>{t('registerForm.parentIdSmall')}</small>
              </div>
            </div>
          )}

          {/* Autre JOFC fan club */}
          <div className="form-section">
            <h2>{t('registerForm.sectionOtherClub')}</h2>
            
            <div className="form-group">
              <label>{t('registerForm.otherClubQ')}</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="otherFanClub"
                    value="oui"
                    checked={formData.otherFanClub === 'oui'}
                    onChange={handleChange}
                    required
                  />
                  <span>{t('registerForm.otherClubYes')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="otherFanClub"
                    value="non"
                    checked={formData.otherFanClub === 'non'}
                    onChange={handleChange}
                    required
                  />
                  <span>{t('registerForm.otherClubNo')}</span>
                </label>
              </div>
              <p
                className="consent-text"
                dangerouslySetInnerHTML={{ __html: t('registerForm.otherClubLegal') }}
              />
            </div>

            {formData.otherFanClub === 'oui' && (
              <div className="alert-box warning">
                <p dangerouslySetInnerHTML={{ __html: t('registerForm.importantUnsub') }} />
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasUnsubscribed"
                      checked={formData.hasUnsubscribed}
                      onChange={handleChange}
                      required={formData.otherFanClub === 'oui'}
                    />
                    <span>{t('registerForm.checkboxUnsub')}</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="register-soci-image-wrapper">
            <img
              src={registerSociImageUrl ? resolveUploadUrl(registerSociImageUrl) : sociImage}
              alt={t('registerForm.altSociImage')}
              className="register-soci-image"
            />
          </div>

          {/* CHOIX DU TYPE D'INSCRIPTION */}
          <div className="form-section">
            <h2>{t('registerForm.membershipChoiceTitle')}</h2>
            
            {formData.suggestedMembershipType && (
              <div className="info-box">
                <p>
                  💡 <strong>{t('registerForm.eligibilityIntro')}</strong>{' '}
                  {formData.suggestedMembershipType === 'under5' && t('registerForm.eligUnder5')}
                  {formData.suggestedMembershipType === 'under14plus' && t('registerForm.eligUnder14')}
                  {formData.suggestedMembershipType === 'adult' &&
                    t('registerForm.eligAdult')}
                </p>
              </div>
            )}
            
            <div className="form-group">
              <label>{t('registerForm.labelYourMembership')}</label>
              <div className="membership-options">
                <label className="radio-label membership-option">
                  <input
                    type="radio"
                    name="membershipType"
                    value="adult"
                    checked={formData.membershipType === 'adult'}
                    onChange={handleChange}
                    disabled={
                      formData.suggestedMembershipType === 'under5' ||
                      formData.suggestedMembershipType === 'under14plus'
                    }
                    required
                  />
                  <div
                    className="option-content membership-option-copy"
                    dangerouslySetInnerHTML={{ __html: t('registerForm.membershipOptAdult') }}
                  />
                </label>

                <label className="radio-label membership-option">
                  <input
                    type="radio"
                    name="membershipType"
                    value="under14plus"
                    checked={formData.membershipType === 'under14plus'}
                    onChange={handleChange}
                    disabled={formData.suggestedMembershipType !== 'under14plus'}
                    required
                  />
                  <div
                    className="option-content membership-option-copy"
                    dangerouslySetInnerHTML={{ __html: t('registerForm.membershipOptUnder14') }}
                  />
                </label>

                <label className="radio-label membership-option">
                  <input
                    type="radio"
                    name="membershipType"
                    value="under5"
                    checked={formData.membershipType === 'under5'}
                    onChange={handleChange}
                    disabled={formData.suggestedMembershipType !== 'under5'}
                    required
                  />
                  <div
                    className="option-content membership-option-copy"
                    dangerouslySetInnerHTML={{ __html: t('registerForm.membershipOptUnder5') }}
                  />
                </label>
              </div>
              <p className="membership-rights-note">{t('registerForm.membershipRightsNote')}</p>
            </div>
          </div>

          {/* PRIX ET PAIEMENT */}
          <div className="form-section">
            <h2>{t('registerForm.sectionPriceTitle')}</h2>
            
            <div className="pricing-table">
              <div className="prix-table-wrapper">
                <table className="prix-registration-table">
                <thead>
                  <tr>
                    <th scope="col">{t('registerForm.thPaymentMode')}</th>
                    <th scope="col">{t('registerForm.thAdult')}</th>
                    <th scope="col">{t('registerForm.thUnder14')}</th>
                    <th scope="col">{t('registerForm.thUnder5')}</th>
                  </tr>
                  <tr>
                    <th scope="col"></th>
                    <th scope="col">{t('registerForm.thPriceChfEur')}</th>
                    <th scope="col">{t('registerForm.thPriceChfEur')}</th>
                    <th scope="col">{t('registerForm.pricingUnder5Cell')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row"><strong>{t('registerForm.pricingRowEbanking')}</strong></th>
                    <td data-label={registrationPricingMobileLabels.adult}>40 CHF / 40 €</td>
                    <td data-label={registrationPricingMobileLabels.under14}>30 CHF / 30 €</td>
                    <td data-label={registrationPricingMobileLabels.under5}>{t('registerForm.pricingUnder5Cell')}</td>
                  </tr>
                  <tr>
                    <th scope="row"><strong>Twint</strong> {t('registerForm.twintCell')}</th>
                    <td data-label={registrationPricingMobileLabels.adult}>40 CHF</td>
                    <td data-label={registrationPricingMobileLabels.under14}>30 CHF</td>
                    <td data-label={registrationPricingMobileLabels.under5}>{t('registerForm.pricingUnder5Cell')}</td>
                  </tr>
                  <tr>
                    <th scope="row"><strong>{t('registerForm.pricingRowRevolut')}</strong></th>
                    <td data-label={registrationPricingMobileLabels.adult}>40 CHF / 40 €</td>
                    <td data-label={registrationPricingMobileLabels.under14}>30 CHF / 30 €</td>
                    <td data-label={registrationPricingMobileLabels.under5}>{t('registerForm.pricingUnder5Cell')}</td>
                  </tr>
                  <tr>
                    <th scope="row"><strong>{t('registerForm.pricingRowCredit')}</strong></th>
                    <td data-label={registrationPricingMobileLabels.adult}>40 CHF / 40 €</td>
                    <td data-label={registrationPricingMobileLabels.under14}>30 CHF / 30 €</td>
                    <td data-label={registrationPricingMobileLabels.under5}>{t('registerForm.pricingUnder5Cell')}</td>
                  </tr>
                </tbody>
              </table>
              </div>
              <p className="table-note">
                {t('registerForm.tableNoteSms')}
              </p>
            </div>

            <div className="form-group">
              <label>{t('registerForm.labelPaymentMode')}</label>
              <select 
                name="paymentMethod" 
                value={formData.paymentMethod} 
                onChange={handleChange} 
                required
                disabled={!formData.membershipType}
              >
                <option value="">{t('registerForm.selectPayment')}</option>
                {formData.membershipType === 'under5' ? (
                  <option value="Gratuit">{t('registerForm.optGratuit')}</option>
                ) : (
                  <>
                    <optgroup label={t('registerForm.optgroupEbanking')}>
                      <option value="E-banking">{t('registerForm.optEbanking')}</option>
                    </optgroup>
                    <optgroup label={t('registerForm.optgroupTwint')}>
                      <option value="Twint">{t('registerForm.optTwint')}</option>
                    </optgroup>
                    <optgroup label={t('registerForm.optgroupRevolut')}>
                      <option value="Carte Revolut">{t('registerForm.optRevolut')}</option>
                    </optgroup>
                    <optgroup label={t('registerForm.optgroupCredit')}>
                      <option value="Carte de crédit">{t('registerForm.optCredit')}</option>
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {formData.membershipType && formData.membershipType !== 'under5' && formData.paymentMethod && (
              <div className="form-group">
                <label>{t('registerForm.labelCurrency')}</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="currency"
                      value="CHF"
                      checked={formData.currency === 'CHF'}
                      onChange={handleChange}
                      disabled={formData.paymentMethod === 'Twint'}
                    />
                    <span>{t('registerForm.chfLabel')}</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="currency"
                      value="EUR"
                      checked={formData.currency === 'EUR'}
                      onChange={handleChange}
                      disabled={formData.paymentMethod === 'Twint'}
                    />
                    <span>{t('registerForm.eurLabel')}</span>
                  </label>
                </div>
              </div>
            )}

            {calculatePrice() !== null && (
              <div className="price-summary">
                <h3>{t('registerForm.amountDue')}</h3>
                <p className="total-price">
                  {calculatePrice()} {formData.currency}
                  {formData.wantsToGetTessera && t('registerForm.tesseraExtra')}
                </p>
              </div>
            )}

            <div className="payment-info">
              <p><strong>{t('registerForm.paymentInfoTitle')}</strong></p>
              <ul>
                <li><strong>{t('registerForm.paymentTwintLi')}</strong> {t('registerForm.paymentTwintContact')}</li>
                <li><strong>{t('registerForm.paymentIbanChf')}</strong> CH52 0900 0000 1747 6158 8</li>
                <li><strong>{t('registerForm.paymentIbanEur')}</strong> CH04 0900 0000 9167 8306 1</li>
                <li><strong>{t('registerForm.paymentBic')}</strong> POFICHBEXXX</li>
                <li><strong>{t('registerForm.paymentBenef')}</strong> {t('registerForm.paymentBenefValue')}</li>
              </ul>
              <p
                className="note"
                dangerouslySetInnerHTML={{ __html: t('registerForm.paymentRevolutNote') }}
              />
            </div>
          </div>

          {/* Décharge - après Prix cotisation */}
          <div className="form-section">
            <h2>{t('registerForm.sectionDischargeTitle')}</h2>
            <div className="form-group consent-group">
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="discharge"
                    value="true"
                    checked={formData.discharge === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, discharge: true }))}
                    required
                  />
                  <span>{t('registerForm.accept')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="discharge"
                    value="false"
                    checked={formData.discharge === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, discharge: false }))}
                  />
                  <span>{t('registerForm.refuse')}</span>
                </label>
              </div>
              <p className="consent-text preview" onClick={() => toggleConsent('discharge')}>
                {t('registerForm.consentDischargePrev')}
                <span className="expand-icon">{expandedConsents.discharge ? '▼' : '▶'}</span>
              </p>
              {expandedConsents.discharge && (
                <p className="consent-text expanded">
                  {t('registerForm.consentDischargeExp')}
                </p>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit-full" disabled={loading}>
              {loading ? t('registerForm.submitLoading') : t('registerForm.submitIdle')}
            </button>
            <p className="form-note">
              {t('registerForm.formNoteSubmit')}
            </p>
          </div>
        </form>

        <p className="auth-link">
          {t('registerForm.alreadyMember')}{' '}
          <Link to="/login">{t('registerForm.linkLogin')}</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterComplete;

